import React, { useEffect, useRef } from 'react';
import { colors, fonts, ScoreColor } from './tokens';

interface HeroOrbProps {
    score: number;
    color: ScoreColor;
    deltaLabel?: string; // e.g. "+6 this week"
    eyebrowLabel?: string; // e.g. "DEVELOPMENT SCORE"
}

const HeroOrb: React.FC<HeroOrbProps> = ({ score, color, deltaLabel, eyebrowLabel = 'DEVELOPMENT SCORE' }) => {
    const size = 380;
    const r = (size - 6) / 2;
    const c = 2 * Math.PI * r;
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    useEffect(() => {
        const cnv = canvasRef.current;

        if (!cnv) return undefined;
        const ctx = cnv.getContext('2d');

        if (!ctx) return undefined;
        const innerSize = size - 24;
        const dpr = window.devicePixelRatio || 1;

        cnv.width = innerSize * dpr;
        cnv.height = innerSize * dpr;
        cnv.style.width = `${innerSize}px`;
        cnv.style.height = `${innerSize}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const h = color.hex.replace('#', '');
        const tint = {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
        };

        const sprite = document.createElement('canvas');

        sprite.width = 64;
        sprite.height = 64;
        const sctx = sprite.getContext('2d');

        if (!sctx) return undefined;
        const sg = sctx.createRadialGradient(32, 32, 0, 32, 32, 32);

        sg.addColorStop(0, 'rgba(255,255,255,1)');
        sg.addColorStop(0.18, `rgba(${tint.r},${tint.g},${tint.b},0.95)`);
        sg.addColorStop(0.45, `rgba(${tint.r},${tint.g},${tint.b},0.35)`);
        sg.addColorStop(1, `rgba(${tint.r},${tint.g},${tint.b},0)`);
        sctx.fillStyle = sg;
        sctx.fillRect(0, 0, 64, 64);

        const radius = innerSize / 2;
        const particles = Array.from({ length: 220 }, () => {
            const theta = Math.random() * Math.PI * 2;
            const phi = Math.acos(2 * Math.random() - 1);
            const u = Math.random();
            const rNorm = 0.55 + Math.sqrt(u) * 0.5;
            const speed = (0.00012 + Math.random() * 0.00026) * (Math.random() < 0.92 ? 1 : -1);

            return {
                theta,
                phi,
                rNorm,
                speed,
                baseSize: 0.5 + Math.random() * 1.7,
                twinkleSpeed: 0.0009 + Math.random() * 0.0022,
                twinklePhase: Math.random() * Math.PI * 2,
            };
        });

        const cx = innerSize / 2;
        const cy = innerSize / 2;
        let last = performance.now();
        let raf = 0;

        const tick = (now: number) => {
            const dt = Math.min(50, now - last);

            last = now;
            ctx.clearRect(0, 0, innerSize, innerSize);
            ctx.globalCompositeOperation = 'lighter';
            for (let i = 0; i < particles.length; i++) {
                const p = particles[i];

                p.theta += p.speed * dt;
                const sinPhi = Math.sin(p.phi);
                const x3 = p.rNorm * radius * sinPhi * Math.cos(p.theta);
                const y3 = p.rNorm * radius * Math.cos(p.phi);
                const z3 = p.rNorm * radius * sinPhi * Math.sin(p.theta);
                const depth = (z3 + radius) / (2 * radius);
                const sizeMul = 0.45 + depth * 1.15;
                const alphaMul = 0.18 + depth * 0.9;
                const tw = 0.55 + 0.45 * Math.sin(now * p.twinkleSpeed + p.twinklePhase);
                const finalAlpha = Math.max(0, Math.min(1, alphaMul * tw));
                const sx = cx + x3;
                const sy = cy + y3;
                const drawSize = p.baseSize * sizeMul * 6;

                ctx.globalAlpha = finalAlpha;
                ctx.drawImage(sprite, sx - drawSize / 2, sy - drawSize / 2, drawSize, drawSize);
            }
            ctx.globalAlpha = 1;
            raf = requestAnimationFrame(tick);
        };

        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [color.hex]);

    return (
        <div
            style={{
                position: 'relative',
                width: size,
                height: size,
                margin: '0 auto',
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    inset: -40,
                    background: `radial-gradient(circle at 50% 50%, ${color.hex}26 0%, ${color.hex}10 30%, transparent 60%)`,
                    filter: 'blur(8px)',
                }}
            />
            <div
                style={{
                    position: 'absolute',
                    inset: 8,
                    borderRadius: '50%',
                    background: `radial-gradient(circle at 35% 28%, ${color.hex}1a, transparent 55%), radial-gradient(circle at 50% 50%, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.85) 100%)`,
                    boxShadow: `inset 0 0 80px rgba(0,0,0,0.6), inset 0 0 6px ${color.hex}33, 0 0 60px ${color.hex}1a`,
                }}
            />
            <canvas
                ref={canvasRef}
                style={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    pointerEvents: 'none',
                }}
            />
            <svg width={size} height={size} style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}>
                <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.05)" strokeWidth="1" fill="none" />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={r}
                    stroke={color.hex}
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={c}
                    strokeDashoffset={c - (score / 100) * c}
                    style={{ filter: `drop-shadow(0 0 4px ${color.hex}88)` }}
                />
            </svg>
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    pointerEvents: 'none',
                }}
            >
                <div
                    style={{
                        fontSize: 11,
                        letterSpacing: '0.22em',
                        color: colors.muted,
                        marginBottom: 6,
                    }}
                >
                    {eyebrowLabel}
                </div>
                <div
                    style={{
                        fontSize: 132,
                        fontWeight: 300,
                        letterSpacing: '-0.05em',
                        lineHeight: 1,
                    }}
                >
                    {score}
                </div>
                <div
                    style={{
                        fontSize: 13,
                        color: color.hex,
                        marginTop: 8,
                        letterSpacing: '0.02em',
                    }}
                >
                    {color.name}
                    {deltaLabel ? ` · ${deltaLabel}` : ''}
                </div>
            </div>
        </div>
    );
};

export default HeroOrb;
